import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import Divider from '@mui/material/Divider';
import { Button, Card, Chip, Grid, Modal, ModalActions, Spinner, Typography, designTokens } from '@platform-ui/design-system';
import React, { useEffect, useState } from 'react';
import Connect from '../../../../Connect/Connect';
import { useStoreDispatch } from '../../../../Store';
import { useToast } from '../../../../ToastProvider';
import { BasicAuth } from '../BasicAuth/BasicAuth';
import { OAuth } from '../OAuth/OAuth';
import { OAuthClientCredentials } from '../OAuthClientCredentials/OAuthClientCredentials';
import { ZDP_AUTH_SETUP } from '../action_types';
import { useFlipperHook } from '../../../../Tables/helpers';

export const Authentication = () => {
  const dispatch = useStoreDispatch();
  const [authSelection, setAuthSelection] = useState('');
  const [savedCreds, setSavedCreds] = useState<any>({});
  const [loading, setLoading] = useState(false);
  // (mlim):Local development: Uses mock Billing server at localhost:5001 to avoid CORS issues
  // Ping me for the script
  const isLocal = window.location.host.includes('localhost');
  const billingHost = isLocal ? 'http://localhost:5001' : '';
  const appsV1CredsPath = isLocal ? `${billingHost}/apps/private/getSfdcCredential` : '/apps/v1/getSfdcCredential';
  const selectOauthMessage = 'Are you sure you want to use OAuth Credentials for sync?';
  const selectBasicMessage = 'Are you sure you want to use Salesforce Credentials for sync?';
  const selectClientCredsMessage = 'Are you sure you want to use OAuth Client Credentials for sync?';
  const [openConfirm, setOpenConfirm] = useState(false);
  const [modalData, setModalData] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { setToast } = useToast();
  const [hideLegacyChip, setHideLegacyChip] = useFlipperHook('hide_legacy_ui_chip');
  const [enableClientCredentialsOnly, setEnableClientCredentialsOnly] = useFlipperHook('enable_client_credentials_only');
  const dacoProxyPath = `${isLocal ? 'http://localhost:8070' : '/platform/gateway-proxy-v2'}`;

  /**
   * Updates the DACO connection with the specified connection method.
   *
   * @param connectionMethod - The authentication method to use for the connection
   * @param tenantId - The tenant ID for the request
   */
  const updateDacoConnection = async (connectionMethod: string, tenantId: string) => {
    try {
      // Step 1: Retrieve user ID from window.connect or cookies
      let zuoraUserIdFromCookie = '';

      if (window.connect?.current_user?.user_id) {
        zuoraUserIdFromCookie = window.connect.current_user.user_id;
      } else {
        const cookies = document.cookie.split(';').map(c => c.trim());
        cookies.forEach(cookie => {
          const [name, value] = cookie.split('=');
          if (name === 'Zuora-User-Id') {
            zuoraUserIdFromCookie = value;
          }
        });
      }

      // Step 2: Call DACO import-connection endpoint with connectionMethod parameter
      // Connection methods: OAUTH_CLIENT_CREDENTIALS, BASIC_AUTH, OAUTH_DEVICE_FLOW
      const headers = {
        'Zuora-Tenant-Id': tenantId,
        'Zuora-User-Id': zuoraUserIdFromCookie,
        'Content-Type': 'application/json'
      };
      const response = await Connect.proxyCall(
        `${dacoProxyPath}/datacon/connections/core/update-connection?connectionMethod=${connectionMethod}`,
        'POST',
        {},
        headers
      );
      if (!response.ok) throw Error(response.statusText);
    } catch (err) {
      Connect.log('Failed to update DACO connection:', err);
      throw err;
    }
  };

  /**
   * Fetches existing credentials from DACO and Billing to determine current auth type.
   */
  const fetchCreds = async () => {
    try {
      setLoading(true);

      // Step 1: Retrieve tenant and user IDs from window.connect or cookies
      let zuoraTenantIdFromCookie = '';
      let zuoraUserIdFromCookie = '';

      // Try to get tenant/user ID from window.connect.tenant first, fallback to cookies
      if (window.connect?.tenant?.tenant_id) {
        zuoraTenantIdFromCookie = window.connect.tenant.tenant_id;
      } else {
        // Try to parse from document.cookie
        const cookies = document.cookie.split(';').map(c => c.trim());
        cookies.forEach(cookie => {
          const [name, value] = cookie.split('=');
          if (name === 'Zuora-Tenant-Id') {
            zuoraTenantIdFromCookie = value;
          }
          if (name === 'Zuora-User-Id') {
            zuoraUserIdFromCookie = value;
          }
        });
      }

      if (window.connect?.current_user?.user_id) {
        zuoraUserIdFromCookie = window.connect.current_user.user_id;
      }

      // Step 2: Check DACO for OAuth Client Credentials
      const headers = {
        'Accept': 'application/json',
        'Zuora-Tenant-Id': zuoraTenantIdFromCookie,
        'Zuora-User-Id': zuoraUserIdFromCookie
      };

      try {
        const dacoResponse = await Connect.proxyCall(
          `${dacoProxyPath}/datacon/connections/connection-credentials`,
          'GET',
          undefined,
          headers
        );

        // Step 3: If DACO has OAUTH_CLIENT_CREDENTIALS, set auth selection to 'clientcreds'
        if (dacoResponse.ok) {
          const connectionData = await dacoResponse.json();
          if (connectionData && connectionData.connectionMethod === 'OAUTH_CLIENT_CREDENTIALS') {
            setSavedCreds({
              externalClientAppId: connectionData.externalClientAppId,
              externalClientAppSecret: connectionData.externalClientAppSecret,
              externalClientLoginUrl: connectionData.externalClientLoginUrl,
              sandbox: connectionData.isSandbox
            });
            setAuthSelection('clientcreds');
            dispatch({ type: ZDP_AUTH_SETUP, payload: { authSet: true } });
            setLoading(false);
            return;
          }
        }
      } catch (dacoErr) {
        // DACO call failed, fall back to Billing logic only if not in client-credentials-only mode
        if (!enableClientCredentialsOnly) {
          Connect.log('DACO connection check failed, falling back to Billing:', dacoErr);
        } else {
          Connect.log('DACO connection check failed, skipping Billing fallback in client-credentials-only mode:', dacoErr);
          setLoading(false);
          return;
        }
      }

      // Step 4: Fall back to Billing API for Basic/OAuth credentials (only if not in client-credentials-only mode)
      const response = await Connect.proxyCall(appsV1CredsPath, 'GET', undefined, {
        'Accept': 'application/json',
      });

      if (!response.ok) throw Error(response.statusText);
      const { data } = await response.json();

      // Step 5: Set auth selection based on oauthEnabled flag
      setSavedCreds(data || {});
      setAuthSelection(data == null ? '' : data.oauthEnabled ? 'oauth' : 'basic');
      if (data) {
        dispatch({ type: ZDP_AUTH_SETUP, payload: { authSet: true } });
      }
    } catch (err) {
      Connect.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreds();
  }, []);

  useEffect(() => {
    if (enableClientCredentialsOnly && authSelection !== 'clientcreds') {
      setAuthSelection('clientcreds');
    }
  }, [enableClientCredentialsOnly, authSelection]);

  return (
    <>
      <Modal
        id='salesforce-auth-confirm-modal'
        open={openConfirm}
        dsOnClose={() => setOpenConfirm(false)}
        disableBackdropClick
        body={<Typography variant='h5' body={modalData === 'basic' ? selectBasicMessage : modalData === 'oauth' ? selectOauthMessage : selectClientCredsMessage} />}
        footer={
          <ModalActions>
            <Button variant='outlined' disabled={confirmLoading} dsOnClick={() => setOpenConfirm(false)}>
              No
            </Button>
            <Button
              disabled={confirmLoading}
              dsOnClick={async () => {
                try {
                  setConfirmLoading(true);

                  // Step 1: Retrieve tenant ID for DACO calls
                  let zuoraTenantIdFromCookie = '';
                  if (window.connect?.tenant?.tenant_id) {
                    zuoraTenantIdFromCookie = window.connect.tenant.tenant_id;
                  } else {
                    const cookies = document.cookie.split(';').map(c => c.trim());
                    cookies.forEach(cookie => {
                      const [name, value] = cookie.split('=');
                      if (name === 'Zuora-Tenant-Id') {
                        zuoraTenantIdFromCookie = value;
                      }
                    });
                  }

                  // Step 2: For client credentials, no need to check sync or call enableOAuth
                  // DACO connection is updated when user saves credentials in OAuthClientCredentials component
                  if (modalData === 'clientcreds') {
                    setOpenConfirm(false);
                    setAuthSelection('clientcreds');
                    setConfirmLoading(false);
                    return;
                  }

                  // Step 3: For basic/oauth, check sync status
                  let response = await fetch(`${billingHost}/apps/SfdcSync.do?method=checkIfSyncInProgress`, {
                    ...(isLocal ? { headers: { 'Host': 'stg2core-int.zuora.com' } } : {})
                  });
                  if (!response.ok) throw Error(response.statusText);
                  const { syncInprogress } = await response.json();

                  if (syncInprogress) {
                    setToast({
                      message: 'Object syncing is in progress. Please try again later!',
                      severity: 'warning'
                    });
                  } else {
                    // Step 4: Call Billing to enable/disable OAuth
                    response = await fetch(`${billingHost}/apps/SfdcSync.do?method=enableOAuth&type=${modalData === 'oauth'}`, {
                      ...(isLocal ? { headers: { 'Host': 'stg2core-int.zuora.com' } } : {})
                    });
                    if (!response.ok) throw Error(response.statusText);

                    setOpenConfirm(false);
                    setAuthSelection(modalData);
                  }
                } catch (err) {
                  setToast({
                    message: err.message || 'Error setting up credentials. Please try again later!',
                    severity: 'error'
                  })
                } finally {
                  setConfirmLoading(false);
                }
              }}
            >
              Yes
            </Button>
          </ModalActions>
        }
      />
      {
        loading ?
        <Spinner /> :
        <Card
          id='authentication-id'
          header={
            <>
              <Grid container alignItems='center'>
                <Grid container item xs direction='column' spacing={0.5}>
                  <Grid container item alignItems='center' spacing={1}>
                    <Grid item><Typography variant='h5'>Salesforce Credentials { !hideLegacyChip && <Chip size='medium' dsOnClick={() => window.location.href = '/apps/SfdcSync.do'} label={<Typography variant='overline'>Back to Legacy UI</Typography>} state='info' />}</Typography></Grid>
                  </Grid>
                  <Grid item><Typography variant='body2'>Set up sync credentials to connect to Salesforce</Typography></Grid>
                </Grid>
              </Grid>
              <Divider style={{marginTop: '20px'}} />
            </>
          }
          body={
            <Grid container>
              <Grid key='auth-selection-key' item xs={12}>
                <>
                  <Grid container>
                    <Grid item xs={6}>
                      <FormControl>
                        <FormLabel>Credentials Type</FormLabel>
                        <RadioGroup
                          row
                          defaultValue={authSelection}
                          value={authSelection}
                        >
                          {!enableClientCredentialsOnly && (
                            <FormControlLabel
                              value='basic'
                              control={<Radio size='medium' />}
                              label='Salesforce'
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setOpenConfirm(true);
                                setModalData(e.target.value);
                              }}
                            />
                          )}
                          {!enableClientCredentialsOnly && (
                            <FormControlLabel
                              value='oauth'
                              control={<Radio size='medium' />}
                              label='OAuth'
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setOpenConfirm(true);
                                setModalData(e.target.value);
                              }} />
                          )}
                          <FormControlLabel
                            value='clientcreds'
                            control={<Radio size='medium' />}
                            label='Client Credentials'
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setOpenConfirm(true);
                              setModalData(e.target.value);
                            }} />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <hr />
                  { authSelection === 'oauth' && <OAuth savedCreds={savedCreds} updateDacoConnection={updateDacoConnection} /> }
                  { authSelection === 'basic' && <BasicAuth savedCreds={savedCreds} updateDacoConnection={updateDacoConnection} /> }
                  { authSelection === 'clientcreds' && <OAuthClientCredentials savedCreds={savedCreds} updateDacoConnection={updateDacoConnection} /> }
                </>
              </Grid>
            </Grid>
          }
        />
      }
    </>
  )
};
